import React from "react";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
  Grid,
  Button,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  DragIndicator,
  Delete,
  Add,
  Remove,
} from "@material-ui/icons";
import RichTextEditor from "../RichTextEditor";
import ImageUploader from "../ImageUploader";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: "relative",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  dragHandle: {
    cursor: "grab",
    "&:active": {
      cursor: "grabbing",
    },
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  arrayItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
}));

const SectionEditor = ({ section, onChange, onDelete, onToggleActive }) => {
  const classes = useStyles();

  const handleFieldChange = (field, value) => {
    onChange({
      ...section,
      data: {
        ...section.data,
        [field]: value,
      },
    });
  };

  const handleArrayItemChange = (arrayField, index, itemField, value) => {
    const newArray = [...section.data[arrayField]];
    newArray[index] = {
      ...newArray[index],
      [itemField]: value,
    };
    handleFieldChange(arrayField, newArray);
  };

  const handleAddArrayItem = (arrayField, template) => {
    const newArray = [...(section.data[arrayField] || []), template];
    handleFieldChange(arrayField, newArray);
  };

  const handleRemoveArrayItem = (arrayField, index) => {
    const newArray = section.data[arrayField].filter((_, i) => i !== index);
    handleFieldChange(arrayField, newArray);
  };

  const renderNavigationEditor = () => (
    <>
      <Typography variant="subtitle2" className={classes.field}>
        Logo
      </Typography>
      <ImageUploader
        currentImage={section.data.logo?.imageUrl}
        onUpload={async (file) => {
          // Implementar upload
          return URL.createObjectURL(file);
        }}
        onRemove={() =>
          handleFieldChange("logo", { ...section.data.logo, imageUrl: "" })
        }
      />
      <TextField
        fullWidth
        label="Texto alternativo del logo"
        value={section.data.logo?.altText || ""}
        onChange={(e) =>
          handleFieldChange("logo", {
            ...section.data.logo,
            altText: e.target.value,
          })
        }
        className={classes.field}
      />
      <Typography variant="subtitle2" className={classes.field}>
        Enlaces de navegación
      </Typography>
      {section.data.links?.map((link, index) => (
        <Box key={index} className={classes.arrayItem}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Texto"
                value={link.text}
                onChange={(e) =>
                  handleArrayItemChange("links", index, "text", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="URL"
                value={link.url}
                onChange={(e) =>
                  handleArrayItemChange("links", index, "url", e.target.value)
                }
              />
            </Grid>
          </Grid>
          <IconButton
            size="small"
            onClick={() => handleRemoveArrayItem("links", index)}
          >
            <Remove />
          </IconButton>
        </Box>
      ))}
      <Button
        startIcon={<Add />}
        onClick={() =>
          handleAddArrayItem("links", { text: "", url: "" })
        }
        className={classes.addButton}
      >
        Agregar enlace
      </Button>
    </>
  );

  const renderHeroEditor = () => (
    <>
      <TextField
        fullWidth
        label="Título"
        value={section.data.title || ""}
        onChange={(e) => handleFieldChange("title", e.target.value)}
        className={classes.field}
      />
      <TextField
        fullWidth
        label="Subtítulo"
        value={section.data.subtitle || ""}
        onChange={(e) => handleFieldChange("subtitle", e.target.value)}
        className={classes.field}
        multiline
        rows={2}
      />
      <Typography variant="subtitle2" className={classes.field}>
        Imagen de fondo
      </Typography>
      <ImageUploader
        currentImage={section.data.backgroundImage}
        onUpload={async (file) => {
          return URL.createObjectURL(file);
        }}
        onRemove={() => handleFieldChange("backgroundImage", "")}
      />
      <TextField
        fullWidth
        label="Texto del botón CTA"
        value={section.data.cta?.text || ""}
        onChange={(e) =>
          handleFieldChange("cta", {
            ...section.data.cta,
            text: e.target.value,
          })
        }
        className={classes.field}
      />
      <TextField
        fullWidth
        label="URL del botón CTA"
        value={section.data.cta?.url || ""}
        onChange={(e) =>
          handleFieldChange("cta", {
            ...section.data.cta,
            url: e.target.value,
          })
        }
        className={classes.field}
      />
    </>
  );

  const renderFeaturesEditor = () => (
    <>
      <TextField
        fullWidth
        label="Título de la sección"
        value={section.data.title || ""}
        onChange={(e) => handleFieldChange("title", e.target.value)}
        className={classes.field}
      />
      <Typography variant="subtitle2" className={classes.field}>
        Características
      </Typography>
      {section.data.features?.map((feature, index) => (
        <Box key={index} className={classes.arrayItem}>
          <TextField
            fullWidth
            label="Icono (emoji o texto)"
            value={feature.icon}
            onChange={(e) =>
              handleArrayItemChange("features", index, "icon", e.target.value)
            }
            className={classes.field}
          />
          <TextField
            fullWidth
            label="Título"
            value={feature.title}
            onChange={(e) =>
              handleArrayItemChange("features", index, "title", e.target.value)
            }
            className={classes.field}
          />
          <TextField
            fullWidth
            label="Descripción"
            value={feature.description}
            onChange={(e) =>
              handleArrayItemChange(
                "features",
                index,
                "description",
                e.target.value
              )
            }
            multiline
            rows={2}
            className={classes.field}
          />
          <IconButton
            size="small"
            onClick={() => handleRemoveArrayItem("features", index)}
          >
            <Remove />
          </IconButton>
        </Box>
      ))}
      <Button
        startIcon={<Add />}
        onClick={() =>
          handleAddArrayItem("features", {
            id: Date.now().toString(),
            icon: "⭐",
            title: "",
            description: "",
          })
        }
        className={classes.addButton}
      >
        Agregar característica
      </Button>
    </>
  );

  const renderTestimonialsEditor = () => (
    <>
      <TextField
        fullWidth
        label="Título de la sección"
        value={section.data.title || ""}
        onChange={(e) => handleFieldChange("title", e.target.value)}
        className={classes.field}
      />
      <Typography variant="subtitle2" className={classes.field}>
        Testimonios
      </Typography>
      {section.data.testimonials?.map((testimonial, index) => (
        <Box key={index} className={classes.arrayItem}>
          <Typography variant="subtitle2">Foto del cliente</Typography>
          <ImageUploader
            currentImage={testimonial.photo}
            onUpload={async (file) => {
              return URL.createObjectURL(file);
            }}
            onRemove={() =>
              handleArrayItemChange("testimonials", index, "photo", "")
            }
          />
          <TextField
            fullWidth
            label="Nombre"
            value={testimonial.name}
            onChange={(e) =>
              handleArrayItemChange(
                "testimonials",
                index,
                "name",
                e.target.value
              )
            }
            className={classes.field}
          />
          <TextField
            fullWidth
            label="Cargo"
            value={testimonial.role}
            onChange={(e) =>
              handleArrayItemChange(
                "testimonials",
                index,
                "role",
                e.target.value
              )
            }
            className={classes.field}
          />
          <TextField
            fullWidth
            label="Testimonio"
            value={testimonial.text}
            onChange={(e) =>
              handleArrayItemChange(
                "testimonials",
                index,
                "text",
                e.target.value
              )
            }
            multiline
            rows={3}
            className={classes.field}
          />
          <IconButton
            size="small"
            onClick={() => handleRemoveArrayItem("testimonials", index)}
          >
            <Remove />
          </IconButton>
        </Box>
      ))}
      <Button
        startIcon={<Add />}
        onClick={() =>
          handleAddArrayItem("testimonials", {
            id: Date.now().toString(),
            photo: "",
            name: "",
            role: "",
            text: "",
          })
        }
        className={classes.addButton}
      >
        Agregar testimonio
      </Button>
    </>
  );

  const renderCTAEditor = () => (
    <>
      <TextField
        fullWidth
        label="Título"
        value={section.data.title || ""}
        onChange={(e) => handleFieldChange("title", e.target.value)}
        className={classes.field}
      />
      <TextField
        fullWidth
        label="Descripción"
        value={section.data.description || ""}
        onChange={(e) => handleFieldChange("description", e.target.value)}
        multiline
        rows={2}
        className={classes.field}
      />
      <TextField
        fullWidth
        label="Texto del botón principal"
        value={section.data.primaryCta?.text || ""}
        onChange={(e) =>
          handleFieldChange("primaryCta", {
            ...section.data.primaryCta,
            text: e.target.value,
          })
        }
        className={classes.field}
      />
      <TextField
        fullWidth
        label="URL del botón principal"
        value={section.data.primaryCta?.url || ""}
        onChange={(e) =>
          handleFieldChange("primaryCta", {
            ...section.data.primaryCta,
            url: e.target.value,
          })
        }
        className={classes.field}
      />
    </>
  );

  const renderFooterEditor = () => (
    <>
      <Typography variant="subtitle2" className={classes.field}>
        Logo del footer
      </Typography>
      <ImageUploader
        currentImage={section.data.logo?.imageUrl}
        onUpload={async (file) => {
          return URL.createObjectURL(file);
        }}
        onRemove={() =>
          handleFieldChange("logo", { ...section.data.logo, imageUrl: "" })
        }
      />
      <TextField
        fullWidth
        label="Copyright"
        value={section.data.copyright || ""}
        onChange={(e) => handleFieldChange("copyright", e.target.value)}
        className={classes.field}
      />
    </>
  );

  const renderEditor = () => {
    switch (section.type) {
      case "navigation":
        return renderNavigationEditor();
      case "hero":
        return renderHeroEditor();
      case "features":
        return renderFeaturesEditor();
      case "testimonials":
        return renderTestimonialsEditor();
      case "cta":
        return renderCTAEditor();
      case "footer":
        return renderFooterEditor();
      default:
        return <Typography>Tipo de sección no soportado</Typography>;
    }
  };

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.title}>
          <IconButton className={classes.dragHandle} size="small">
            <DragIndicator />
          </IconButton>
          <Typography variant="h6">
            {section.type.charAt(0).toUpperCase() + section.type.slice(1)}
          </Typography>
        </Box>
        <Box className={classes.actions}>
          <FormControlLabel
            control={
              <Switch
                checked={section.active}
                onChange={onToggleActive}
                color="primary"
              />
            }
            label="Activa"
          />
          <IconButton onClick={onDelete} color="secondary">
            <Delete />
          </IconButton>
        </Box>
      </Box>
      {renderEditor()}
    </Paper>
  );
};

export default SectionEditor;
